import { useSyncExternalStore } from "react";
import moment from "moment";
import { fetchWithToken, patchWithToken } from "api/ApiHandler";
import { handleAuthError } from "../userManagement/shared";

// Service-provider applications (a provider registers their business and
// uploads documents; an admin approves or rejects it).
//
// Pending applications come from GET /admin/pending-service-providers; approve
// is a PATCH on /admin/approve-service-provider, and reject / revoke a PATCH on
// /admin/reject-service-provider with the reason. Approved / Rejected come from
// GET /admin/get-all-service-providers (by status), plus any decisions made in
// this session.

export const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"];

export const REJECT_REASONS = [
  "Documents are unclear or unreadable",
  "Trade license has expired",
  "VAT / TRN number doesn’t match the certificate",
  "Business details are incomplete",
  "Duplicate application",
];

const formatTime = (t) => (moment(t, "HH:mm", true).isValid() ? moment(t, "HH:mm").format("h:mm A") : t);

const formatHours = (h) => {
  if (!h) return "";
  const days = (h.openDays || []).map((d) => String(d).slice(0, 3));
  const daysLabel =
    days.length === 7 ? "Daily" :
    days.length > 2 ? `${days[0]} – ${days[days.length - 1]}` :
    days.join(", ");
  const time = h.open24Hours ? "Open 24 hours" : [h.opensAt, h.closesAt].filter(Boolean).map(formatTime).join(" – ");
  return [daysLabel, time].filter(Boolean).join(" · ");
};

const uniqueBy = (list, key) => {
  const seen = new Set();
  return (list || []).filter((x) => {
    const k = key(x);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// Shop workingHours: [{ day, startTime, endTime, isOpen }] → "Mon – Fri · 9:00 AM – 7:00 PM"
const formatWorkingHours = (list) => {
  const open = uniqueBy(list, (w) => w.day).filter((w) => w.isOpen !== false);
  if (!open.length) return "";
  const days = open.map((w) => String(w.day).slice(0, 3));
  const daysLabel =
    days.length === 7 ? "Daily" :
    days.length > 2 ? `${days[0]} – ${days[days.length - 1]}` :
    days.join(", ");
  const ranges = uniqueBy(open, (w) => `${w.startTime}-${w.endTime}`).map((w) => [w.startTime, w.endTime].filter(Boolean).map(formatTime).join(" – "));
  return [daysLabel, ranges.length === 1 ? ranges[0] : "Varies by day"].filter(Boolean).join(" · ");
};

const findEmirate = (...texts) => {
  const text = texts.filter(Boolean).join(" ").toLowerCase();
  return EMIRATES.find((e) => text.includes(e.toLowerCase())) || "";
};

// The API returns duplicate rows in some shop arrays (hours, services, links), so they're de-duplicated here
const toShop = (s) => {
  const primary = (s.locations || []).find((l) => l.isPrimary) || s.locations?.[0] || {};
  const services = uniqueBy(s.services, (x) => `${x.title}|${x.serviceName}`).map((x) => ({
    title: x.title || x.serviceName || x.serviceTypeId?.name || "Service",
    type: x.serviceTypeId?.name || x.serviceName || "",
    description: x.description || "",
    image: x.serviceImage || null,
    available: x.isAvailable !== false,
  }));
  return {
    id: s._id,
    name: s.shopName || "Unnamed shop",
    image: s.shopImage || null,
    address: s.businessLocation?.address || primary.address || "",
    serviceTypes: uniqueBy(s.serviceType, (t) => t.name || t).map((t) => t.name || t),
    services,
    hours: formatWorkingHours(s.workingHours),
    socialLinks: uniqueBy(s.socialLinks, (l) => l.url),
  };
};

// Maps an API service provider (signup shape + its shops) onto what the cards/modal render
export const toApplication = (p) => {
  const docs = p.documents || {};
  const signatory = p.authorizedSignatory || {};
  const expiry = p.licenseExpiry;
  const shops = (p.shops || []).map(toShop);
  const location = p.businessAddress || shops.find((s) => s.address)?.address || "";
  const services = p.servicesOffered?.length
    ? p.servicesOffered
    : [...new Set(shops.flatMap((s) => [...s.serviceTypes, ...s.services.map((x) => x.type || x.title)]).filter(Boolean))];
  const status = (p.status || "pending").toLowerCase();
  return {
    id: p._id || p.id,
    title: p.businessName || "Unnamed business",
    owner: signatory.fullName || p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" "),
    position: signatory.position || "",
    email: p.businessEmail || signatory.email || p.email || "",
    phone: p.contactNumber || p.phNumber || p.phone || "",
    emirate: p.emirate || findEmirate(location),
    location,
    image: shops.find((s) => s.image)?.image || null,
    vat: p.vatTrnNumber || "",
    license: p.tradeLicenseNo || "",
    licenseExpiry: expiry || null,
    services,
    hours: p.operatingHours?.openDays?.length ? formatHours(p.operatingHours) : shops.find((s) => s.hours)?.hours || "",
    shops,
    submittedAt: p.createdAt,
    reviewedAt: p.reviewedAt || (status !== "pending" ? p.updatedAt : null) || null,
    rejectReason: p.rejectReason || p.reason || "",
    // get-all-service-providers calls an approved provider "active"
    status: status === "active" ? "approved" : status,
    // Only documents that were actually uploaded
    documents: [
      { key: "trade", label: "Trade License", note: expiry ? `Expires ${moment(expiry).format("DD MMM YYYY")}` : "", url: docs.tradeLicense || null },
      { key: "vat", label: "VAT Certificate", note: "Federal Tax Authority", url: docs.vatCertificate || null },
      { key: "eidFront", label: "Emirates ID (Front)", note: "Authorised signatory", url: docs.emiratesIdFront || null },
      { key: "eidBack", label: "Emirates ID (Back)", note: "Authorised signatory", url: docs.emiratesIdBack || null },
    ].filter((d) => d.url),
  };
};

let state = { items: [], loading: false, loaded: false, error: "" };
const listeners = new Set();
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
};

const toList = (data) => (Array.isArray(data) ? data : data?.providers || data?.serviceProviders || data?.data || []);

export async function loadApplications() {
  setState({ loading: true, error: "" });
  try {
    // Approved / rejected providers come from the all-providers list; it's optional, pending still loads without it
    const [pendingData, allData] = await Promise.all([
      fetchWithToken("/admin/pending-service-providers"),
      fetchWithToken("/admin/get-all-service-providers").catch((error) => {
        console.error("Fetch all service providers error:", error.response?.data || error);
        return [];
      }),
    ]);
    const pending = toList(pendingData).map((p) => toApplication({ ...p, status: "pending" }));
    const pendingIds = new Set(pending.map((a) => a.id));
    const reviewedFromApi = toList(allData)
      .map(toApplication)
      .filter((a) => !pendingIds.has(a.id) && (a.status === "approved" || a.status === "rejected"));
    const fetched = [...pending, ...reviewedFromApi];
    // Keep this session's approve/reject decisions (rejected providers may be deleted server-side)
    const reviewed = state.items.filter((a) => a.status !== "pending" && !fetched.some((f) => f.id === a.id && f.status !== "pending"));
    const reviewedIds = new Set(reviewed.map((a) => a.id));
    setState({ items: [...fetched.filter((a) => !reviewedIds.has(a.id)), ...reviewed], loading: false, loaded: true });
  } catch (error) {
    console.error("Fetch pending service providers error:", error.response?.data || error);
    if (handleAuthError(error)) return;
    setState({ loading: false, loaded: true, error: "Unable to load applications." });
  }
}

export function useApplicationsState() {
  return useSyncExternalStore(subscribe, () => state);
}

export function useApplications() {
  return useApplicationsState().items;
}

export function updateApplication(id, patch) {
  setState({ items: state.items.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
}

export async function approveApplication(id) {
  await patchWithToken(`/admin/approve-service-provider/${id}`, { approve: true });
  updateApplication(id, { status: "approved", reviewedAt: new Date().toISOString(), rejectReason: "" });
}

export async function rejectApplication(id, reason) {
  await patchWithToken(`/admin/reject-service-provider/${id}`, { reason });
  updateApplication(id, { status: "rejected", reviewedAt: new Date().toISOString(), rejectReason: reason });
}

export const isPdf = (url) => /\.pdf($|\?)/i.test(url || "");

export const isLicenseExpired = (a) => !!a.licenseExpiry && moment(a.licenseExpiry).isBefore(moment());
export const licenseExpiresSoon = (a) => !!a.licenseExpiry && !isLicenseExpired(a) && moment(a.licenseExpiry).isBefore(moment().add(60, "days"));
